import { ApiService, TApiServiceError, TApiServiceOptions } from "../apiService";
import { TLegalAcceptanceInput } from "../legal";
import {
  TAuthLoginRequestPayload,
  TAuthLoginResponsePayload,
  TAuthResetPasswordRequestPayload,
  TAuthSignupRequestPayload,
  TForgotPasswordRequestPayload,
} from "./auth.types";

import { handleAsync } from "../../utils";
import { getJwtExpiration } from "../../utils/jwt";
import { invalidateSessionForLogout } from "../../utils/tokenRefresh";
import { tokenStorage } from "../../utils/tokenStorage";
import { withAuthHooks } from "./auth.ky.hooks";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

class AuthService {
  rootEndpoint = `auth` as const;
  loginEndpoint = `${this.rootEndpoint}/login` as const;
  logoutEndpoint = `${this.rootEndpoint}/logout` as const;
  signupEndpoint = `${this.rootEndpoint}/register` as const;
  forgotPasswordEndpoint = `${this.rootEndpoint}/restore-password` as const;
  resetPasswordEndpoint = `${this.rootEndpoint}/reset-password` as const;

  // methods
  async login(payload: TAuthLoginRequestPayload, options?: TApiServiceOptions) {
    const apiPayload = {
      email: payload.email,
      password: payload.password,
    };

    const { data, error } = await handleAsync<TApiServiceError, TAuthLoginResponsePayload>(() =>
      apiService.post(this.loginEndpoint, apiPayload, options)
    );

    if (data) {
      tokenStorage.setAccessToken(data.data.access_token);
      // Store refresh token if available
      if (data.data.refresh_token?.token) {
        tokenStorage.setRefreshToken(data.data.refresh_token.token);
      }
      // Decode JWT to get access token expiration
      const accessTokenExpiration: number | null = getJwtExpiration(data.data.access_token);
      if (accessTokenExpiration !== null) {
        tokenStorage.setExpiresAt(accessTokenExpiration);
      }
    }

    return { data, error };
  }

  resetPassword(payload: TAuthResetPasswordRequestPayload, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, void>(() => apiService.post(this.resetPasswordEndpoint, payload, options));
  }
  signup(
    payload: TAuthSignupRequestPayload,
    legalAcceptances: TLegalAcceptanceInput[],
    options?: TApiServiceOptions,
    inviteToken?: string
  ) {
    const {
      firstName,
      lastName,
      phone,
      acceptTerms: _acceptTerms,
      confirmPassword: _confirmPassword,
      ...rest
    } = payload;
    const apiPayload = {
      ...rest,
      first_name: firstName,
      last_name: lastName,
      phone_number: phone,
      legal_acceptances: legalAcceptances,
      // Investor invitation (006/RD-4): pre-verifies the invited email server-side.
      ...(inviteToken ? { invite_token: inviteToken } : {}),
    };

    return handleAsync<TApiServiceError, TAuthLoginResponsePayload>(() =>
      apiService.post(this.signupEndpoint, apiPayload, options)
    );
  }

  forgotPassword(payload: TForgotPasswordRequestPayload, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, void>(() => apiService.post(this.forgotPasswordEndpoint, payload, options));
  }

  async logout(options?: TApiServiceOptions) {
    // Attempt to call logout endpoint, but always clear cookies regardless of response
    // This ensures local state is cleared even if server-side logout fails
    const refreshToken = tokenStorage.getRefreshToken();
    const logoutPayload = refreshToken ? { refresh_token: refreshToken } : {};

    // Stop any in-flight silent refresh from re-storing tokens after we clear them.
    invalidateSessionForLogout();

    try {
      const response = await apiService.post<void>(this.logoutEndpoint, logoutPayload, options);
      // Clear cookies on successful logout
      tokenStorage.clearAuthCookies();
      return response;
    } catch (error) {
      // Even if API call fails, clear cookies to ensure local logout
      tokenStorage.clearAuthCookies();
      // Re-throw the error so the caller can handle it if needed
      throw error;
    }
  }
}

export const authService = new AuthService();
